import { useEffect, useState } from 'react';
import type { Language } from '@packages/config';
import type { IntlShape } from 'react-intl';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Box,
  Card,
  CardContent,
  Notification,
  Stack,
  TextField,
  ToggleButtonGroup,
  Button,
  useDeviceType,
  Link,
  useI18n,
} from '@packages/shared';
import type { CustomerDataWithStatus } from '@packages/utilities';
import { useConfig, useCustomer } from '@packages/utilities';
import type { GTMEventGlycerinDisplayError, GTMEventGlycerinDisplayForm } from '@packages/tracking';
import { useTracking, GlycerinErrorSeverity } from '@packages/tracking';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import type { SchemaDescription } from 'yup';
import * as yup from 'yup';
import { useSearchParams, usePathname, useRouter } from 'next/navigation';
import { rot47 } from '../../../utils';
import { Modal } from '../../Modal';
import type { NewsletterSubscribePageProps } from '../types';
import { InputIDs } from '../types';
import { noSpecCharsNoNumbers } from '../helpers/validationSchema';
import {
  formMessages,
  generalFormMessages,
  salutationSelectMessages,
  specialErrorMessages,
} from '../messages';
import { ActivityOverlay } from '../ActivityOverlay';
import { SuccessPage } from '../SuccessPage';
import { basicContainer, smallBasicContainer, stylePanel } from '../styles';
import {
  pagesMessages,
  privacyInfoMessages,
  subscribeMessages,
  voucherModalMessages,
} from './helpers/messages';
import IconNlSubscribe1Default from './animated/IconNlSubscribe1Default';
import { NewsletterSubscribeFormTestIds } from './NewsletterSubscribeFormTestIds';
import type { ConvertedFormNLDataType } from '../GeneralForm/helpers/handleSubmit';
import { submitMessage } from '../GeneralForm/helpers/handleSubmit';
import { useFormDeviceType } from '../GeneralForm/helpers/useFormDeviceType';

type InputID = 'salutation' | 'firstname' | 'lastname' | 'email';
type SetErrorType = `root.${InputID}` | 'root';
type FormValues = {
  [InputIDs.SALUTATION]?: string;
  [InputIDs.FIRSTNAME]?: string;
  [InputIDs.LASTNAME]?: string;
  [InputIDs.EMAIL]: string;
};

type Page1FormProps = NewsletterSubscribePageProps & {
  areaKey: string;
  setPage: (page: number) => void;
  conditionsLabel?: string;
  conditionsText?: string;
};
const genderSalutationMap = {
  m: 'male',
  f: 'female',
  d: 'notSpecified',
};
const getDefaultValues = (customer: CustomerDataWithStatus | undefined): FormValues => ({
  [`${InputIDs.FIRSTNAME}`]: customer?.firstName || '',
  [`${InputIDs.LASTNAME}`]: customer?.lastName || '',
  [`${InputIDs.EMAIL}`]: customer?.email || '',
  [`${InputIDs.SALUTATION}`]: customer?.gender && genderSalutationMap[customer.gender],
});

export const convertFormDataToPostData = (
  formValues: FormValues,
  areaKey: string,
): ConvertedFormNLDataType => {
  const salutation = formValues[InputIDs.SALUTATION];
  return {
    emailAddress: formValues[InputIDs.EMAIL] || '',
    firstName: formValues[InputIDs.FIRSTNAME] || '',
    lastName: formValues[InputIDs.LASTNAME] || '',
    ...(salutation &&
      salutation !== 'notSpecified' && {
        salutation,
      }),
    areaKey,
  };
};

const newsletterSubscribeFormSchema = (intl: IntlShape) =>
  yup.object().shape({
    [InputIDs.FIRSTNAME]: yup
      .string()
      .matches(noSpecCharsNoNumbers, intl.formatMessage(specialErrorMessages.noSpecCharsNoNumbers)),
    [InputIDs.LASTNAME]: yup
      .string()
      .matches(noSpecCharsNoNumbers, intl.formatMessage(specialErrorMessages.noSpecCharsNoNumbers)),
    [InputIDs.EMAIL]: yup
      .string()
      .email(intl.formatMessage(formMessages[InputIDs.EMAIL].error.valid))
      .required(intl.formatMessage(formMessages[InputIDs.EMAIL].error)),
  });

const ModalContent = ({ conditionsText }: { conditionsText?: string }) => {
  const { language, defaultLanguage } = useI18n();
  const {
    host: { domain },
    forms: { newsletterSubscribe },
  } = useConfig();

  const { voucherLink } = newsletterSubscribe[(language ?? defaultLanguage) as Language];

  if (conditionsText) {
    return (
      <span
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: conditionsText,
        }}
      />
    );
  }

  return (
    <FormattedMessage
      id={voucherModalMessages.content.id}
      defaultMessage={voucherModalMessages.content.defaultMessage}
      values={{
        aDomain: (chunks) => <a href={`https://${domain}`}>{chunks}</a>,
        aVoucher: (chunks) => <a href={voucherLink}>{chunks}</a>,
        h5: (chunks) => <h5>{chunks}</h5>,
      }}
    />
  );
};

export const Page1Form = ({
  areaKey,
  setPage,
  headline,
  text,
  conditionsLabel,
  conditionsText,
  buttonLabel,
  buttonLabelMobile,
}: Page1FormProps) => {
  const intl = useIntl();
  const { formatMessage } = intl;
  const { customer } = useCustomer();
  const dispatchGTMEvent = useTracking();
  const { isMobile } = useDeviceType();
  const { language, defaultLanguage, localizeUrl } = useI18n();
  const query = Object.fromEntries(useSearchParams()?.entries() ?? []);
  const pathname = usePathname();
  const router = useRouter();

  const [errorNotification, setErrorNotification] = useState({ show: false, message: '' });
  const [modalOpen, setModalOpen] = useState(false);
  const schema = newsletterSubscribeFormSchema(intl);
  const { register, formState, handleSubmit, control, setValue, setError, getValues } =
    useForm<FormValues>({
      mode: 'onTouched',
      resolver: yupResolver(schema),
      defaultValues: getDefaultValues(customer),
    });
  const {
    forms: {
      apiAgnitasUrl: {
        values: { useDOI },
      },
      newsletterSubscribe,
    },
  } = useConfig();

  const formDeviceType = useFormDeviceType();

  const { errors, isValid, isSubmitting } = formState;
  const submitForm = (data: FormValues) =>
    submitMessage<SetErrorType>({
      endpoint: 'subscribe',
      intl,
      formDeviceType,
      setError,
      convertedFormNLData: convertFormDataToPostData(data, areaKey),
      onSuccess: (resultType: string) => {
        if (resultType === 'ALREADY_SUBSCRIBED') {
          // go to Page 4 if user is already subscribed
          setPage(4);
        } else {
          // go to Page 2 if Double Opt-In is used, otherwise to Page 3
          setPage(useDOI ? 2 : 3);
        }

        // add email as token to 'page 3' or 'SuccessPage' (Tenants without DOI) to be able to track e-mail-address
        const email = getValues('email');
        const token = email && encodeURIComponent(rot47(email));
        const tmpUrl = `${pathname}?${new URLSearchParams({
          ...query,
          token,
        })}`;
        const routerPushUrl = localizeUrl(tmpUrl, language);
        router.push(routerPushUrl);

        dispatchGTMEvent({
          event: 'ga_event',
          eventCategory: 'User Interactions',
          eventLabel: 'newsletter',
          eventAction: 'subscribe',
        });
      },
      onError: (errorMsg: string) => {
        setErrorNotification({ show: true, message: errorMsg });
        dispatchGTMEvent<GTMEventGlycerinDisplayError>({
          event: 'DisplayError',
          DisplayErrorData: {
            category: 'newsletter-subscribe',
            label: 'registration',
            detail: 'error',
            errors: [
              {
                errorType: 'formValidation',
                message: errorMsg,
                severity: GlycerinErrorSeverity.WARNING,
                source: 'newsletter registration form',
              },
            ],
          },
        });
      },
    });

  useEffect(
    () => {
      dispatchGTMEvent<GTMEventGlycerinDisplayForm>({
        event: 'DisplayForm',
        DisplayFormData: {
          category: 'newsletter-subscribe',
          label: 'registration',
        },
      });
    },
    // INSPIRE-3537 - only fire once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  /**
   * Gather the basic props (that only depend on the id) for each input
   */
  const getFieldProps = (inputId: InputID) => ({
    ...register(inputId),
    id: inputId,
    helperText: errors[inputId]?.message,
    label: formatMessage(formMessages[inputId]?.label),
    error: !!errors[inputId],
    placeholder:
      formMessages[inputId].placeholder.defaultMessage !== ''
        ? formatMessage(formMessages[inputId].placeholder)
        : undefined,
    required: !(schema.describe().fields[inputId] as SchemaDescription).optional,
  });

  const customButtonLabel = isMobile ? buttonLabelMobile : buttonLabel;
  const customButtonLabelFinal = isValid
    ? customButtonLabel
    : formatMessage(subscribeMessages.submitDisabled);
  const submitButtonLabel =
    customButtonLabelFinal ||
    formatMessage(isValid ? subscribeMessages.submit : subscribeMessages.submitDisabled);
  const unsubscribeLink =
    newsletterSubscribe[(language ?? defaultLanguage) as Language]?.unsubscribeLink;

  return (
    <ActivityOverlay showLoader={isSubmitting}>
      <Notification
        open={errorNotification.show}
        severity="error"
        autoHideDuration={3500}
        onClose={() => setErrorNotification({ ...errorNotification, show: false })}
      >
        {errorNotification.message}
      </Notification>

      <Modal
        title={conditionsLabel || formatMessage(voucherModalMessages.title)}
        openState={{ isOpen: modalOpen, setIsOpen: setModalOpen }}
      >
        <ModalContent conditionsText={conditionsText} />
      </Modal>

      <SuccessPage
        icon={<IconNlSubscribe1Default sx={{ fontSize: '8rem', mb: 3 }} />}
        headline={headline || formatMessage(pagesMessages.page1.headline)}
        description={text || formatMessage(pagesMessages.page1.description)}
      />
      <form
        onSubmit={handleSubmit(submitForm)}
        noValidate
        data-testid={NewsletterSubscribeFormTestIds.form}
      >
        <Box sx={stylePanel} data-testid={`${NewsletterSubscribeFormTestIds.page1}-${areaKey}`}>
          <Card sx={smallBasicContainer}>
            <CardContent sx={{ padding: 4 }}>
              <Stack spacing={4}>
                <Controller
                  control={control}
                  name="salutation"
                  render={({ field }) => (
                    <ToggleButtonGroup
                      id={InputIDs.SALUTATION}
                      label={formatMessage(formMessages[InputIDs.SALUTATION].label)}
                      options={(['notSpecified', 'female', 'male'] as const).map((value) => ({
                        value,
                        label: formatMessage(salutationSelectMessages[value]),
                      }))}
                      value={field.value}
                      handleChange={(_e, value) => setValue(field.name, value as never)}
                    />
                  )}
                />
                <TextField {...getFieldProps(InputIDs.FIRSTNAME)} />
                <TextField {...getFieldProps(InputIDs.LASTNAME)} />
                <TextField
                  {...getFieldProps(InputIDs.EMAIL)}
                  placeholder={formatMessage(formMessages[InputIDs.EMAIL].placeholder)}
                  required
                />
                <Box sx={{ typography: 'body2', a: { color: 'text.dark' } }}>
                  <FormattedMessage
                    id={subscribeMessages.confirmation.id}
                    defaultMessage={subscribeMessages.confirmation.defaultMessage}
                    values={{
                      a: (chunks) => <a href={unsubscribeLink}>{chunks}</a>,
                    }}
                  />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Box>

        <Box
          sx={{
            ...smallBasicContainer,
            marginTop: 1,
            marginBottom: 5,
            typography: 'body2',
            color: 'text.darkTransparent',
          }}
        >
          {formatMessage(generalFormMessages.mandatory)}
        </Box>

        <Box sx={{ ...basicContainer, textAlign: 'center', marginTop: 2 }}>
          <Button
            data-testid={NewsletterSubscribeFormTestIds.button}
            disabled={!isValid}
            size="large"
            fullWidth={isMobile}
            color="primary"
            type={isValid ? 'submit' : 'button'}
            clickTrackingProps={{
              category: 'newsletter-subscribe',
              label: 'registration',
              detail: 'send',
              custom: { areaKey },
            }}
          >
            {submitButtonLabel}
          </Button>

          {/* Link to open modal with voucher conditions */}
          <Box
            data-testid={NewsletterSubscribeFormTestIds.modallink}
            sx={{
              mt: 3.5,
              mb: 2,
              textDecoration: 'underline',
              cursor: 'pointer',
              typography: 'body3',
            }}
            onClick={() => setModalOpen(true)}
          >
            {conditionsLabel || formatMessage(voucherModalMessages.linkText)}
          </Box>

          {/* Link to open privacy information */}
          <Link
            href={formatMessage(privacyInfoMessages.link)}
            color="inherit"
            sx={{ typography: 'body3' }}
          >
            {formatMessage(privacyInfoMessages.text)}
          </Link>
        </Box>
      </form>
    </ActivityOverlay>
  );
};
